import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  url: string,
  options: RequestInit = {},
): Promise<any> {
  // Set default headers
  const headers = new Headers(options.headers);
  
  // Set Content-Type to JSON if body is present and no Content-Type is set
  if (options.body && !headers.get('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // JWT token is sent automatically via httpOnly cookie
  // No need to manually add Authorization header

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });

  // Handle 401 Unauthorized - redirect to login for authenticated routes
  if (res.status === 401) {
    // Only redirect to login if this is an authenticated route that's not login/register
    if (url.includes('/api/auth/user') || (url.includes('/api/') && !url.includes('/api/auth/login') && !url.includes('/api/auth/register'))) {
      window.location.href = '/login';
    }
  }

  await throwIfResNotOk(res);
  
  // Check if response has content before parsing JSON
  const contentLength = res.headers.get('content-length');
  if (contentLength === '0' || res.status === 204) {
    return null;
  }

  return res.json();
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    
    // JWT token is sent automatically via httpOnly cookie
    const headers = new Headers();

    const res = await fetch(url, {
      headers,
      credentials: "include",
    });

    // Handle 401 Unauthorized
    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
      // Only redirect to login if this is an authenticated route
      if (url.includes('/api/auth/user') || (url.includes('/api/') && !url.includes('/api/auth/login') && !url.includes('/api/auth/register'))) {
        window.location.href = '/login';
      }
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
